import { ParsedData, Question, ModelResponse, Entity } from '../types';

export function parseNERFile(content: string): ParsedData {
  console.log('Parsing file content, length:', content.length);
  
  const lines = content.split('\n');
  const questions: Question[] = [];
  const modelNames = new Set<string>();
  let currentModelName: string | null = null;
  let currentQuestion: Question | null = null;
  let questionCounter = 0;
  let pendingInferenceTime: number | undefined = undefined;
  
  // Track all questions across models to group them
  const questionMap = new Map<string, Question>();

  console.log('Total lines:', lines.length);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines and system messages
    if (!line || 
        line.startsWith('Asking to truncate') || 
        line.startsWith('You seem to be using')) {
      continue;
    }
    
    // Check for inference time
    if (line.toLowerCase().startsWith('inference time:')) {
      const timeMatch = line.match(/inference time:\s*([\d.]+)/i);
      if (timeMatch) {
        pendingInferenceTime = parseFloat(timeMatch[1]);
        console.log('Found inference time:', pendingInferenceTime);
      }
      continue;
    }

    // Check for model separator
    if (line === '====================') {
      console.log('Found model separator');
      currentModelName = null;
      currentQuestion = null;
      continue;
    }

    // Check for question separator
    if (line === '----------') {
      currentQuestion = null;
      continue;
    }

    // Check for model name
    if (line.startsWith('Model:')) {
      currentModelName = line.substring(6).trim();
      console.log('Found model:', currentModelName);
      modelNames.add(currentModelName);
      currentQuestion = null;
      continue;
    }

    // Check for question (example)
    if (line.startsWith('Example:')) {
      const questionText = line.substring(8).trim();
      console.log('Found question:', questionText);
      
      // Find or create question
      if (questionMap.has(questionText)) {
        currentQuestion = questionMap.get(questionText)!;
        console.log('Reusing existing question');
      } else {
        currentQuestion = {
          id: `q${++questionCounter}`,
          text: questionText,
          modelResponses: []
        };
        questionMap.set(questionText, currentQuestion);
        questions.push(currentQuestion);
        console.log('Created new question with id:', currentQuestion.id);
      }
      continue;
    }

    // Check for entity (format: "text - label" or "text - label - (start, end) - [score]")
    if (currentQuestion && currentModelName && line.includes(' - ')) {
      // Split by " - " to get components
      const parts = line.split(' - ').map(p => p.trim());
      
      if (parts.length < 2) continue;
      
      const entityText = parts[0];
      const label = parts[1];
      let start: number | undefined;
      let end: number | undefined;
      let score: number | undefined;
      
      // Check for position info: (start, end)
      if (parts.length >= 3) {
        const posMatch = parts[2].match(/^\((\d+),\s*(\d+)\)$/);
        if (posMatch) {
          start = parseInt(posMatch[1], 10);
          end = parseInt(posMatch[2], 10);
        }
      }
      
      // Check for score info: [score]
      if (parts.length >= 4) {
        const scoreMatch = parts[3].match(/^\[([0-9.eE+-]+)\]$/);
        if (scoreMatch) {
          score = parseFloat(scoreMatch[1]);
        }
      }
      
      // Find or create model response for this question
      let modelResponse = currentQuestion.modelResponses.find(
        mr => mr.modelName === currentModelName
      );
      
      if (!modelResponse) {
        modelResponse = {
          modelName: currentModelName,
          entities: [],
          inferenceTime: pendingInferenceTime
        };
        currentQuestion.modelResponses.push(modelResponse);
        console.log('Created model response for', currentModelName, 'in question', currentQuestion.id, 'with inference time:', pendingInferenceTime);
        pendingInferenceTime = undefined; // Reset after using
      }
      
      const entity: Entity = {
        text: entityText,
        label: label,
        start,
        end,
        score
      };
      modelResponse.entities.push(entity);
      console.log('Added entity:', entityText, '-', label, start !== undefined ? `(${start},${end})` : '', score !== undefined ? `[${score}]` : '');
    }
  }

  console.log('Parse complete. Total questions:', questions.length);
  console.log('Model names found:', Array.from(modelNames));
  
  // Log statistics for each question
  questions.forEach(q => {
    console.log(`Question ${q.id}: ${q.modelResponses.length} model responses`);
  });

  return {
    questions,
    modelNames: Array.from(modelNames)
  };
}

// Parse JSON export format (model_results_*.json)
export function parseNERJson(content: string): ParsedData {
  const raw = JSON.parse(content);

  const questions: Question[] = [];
  const modelNames = new Set<string>();

  if (!raw || !Array.isArray(raw.questions)) {
    throw new Error('Invalid JSON format: missing questions array');
  }

  raw.questions.forEach((q: any, idx: number) => {
    const questionId: string = q.id || `q${idx + 1}`;
    const text: string = q.text || '';

    // Human annotations (optional)
    const humanAnnSrc: any[] = q.humanAnnotations || q.annotations || q.gold || [];
    const humanAnnotations: Entity[] = Array.isArray(humanAnnSrc)
      ? humanAnnSrc.map((e: any) => ({
          text: String(e.text ?? ''),
          label: String(e.label ?? ''),
          start: typeof e.start === 'number' ? e.start : undefined,
          end: typeof e.end === 'number' ? e.end : undefined,
          score: typeof e.score === 'number' ? e.score : (typeof e.confidence === 'number' ? e.confidence : undefined)
        }))
      : [];

    // Support both 'modelResponses' (new schema) and 'models' (legacy)
    const modelResponsesArray = q.modelResponses || q.models || [];
    const modelResponses: ModelResponse[] = Array.isArray(modelResponsesArray)
      ? modelResponsesArray.map((m: any) => {
          const name: string = m.modelName || 'Unknown Model';
          modelNames.add(name);
          const entities: Entity[] = Array.isArray(m.entities)
            ? m.entities.map((e: any) => ({
                text: String(e.text ?? ''),
                label: String(e.label ?? ''),
                start: typeof e.start === 'number' ? e.start : undefined,
                end: typeof e.end === 'number' ? e.end : undefined,
                score: typeof e.score === 'number' ? e.score : (typeof e.confidence === 'number' ? e.confidence : undefined)
              }))
            : [];

          const response: ModelResponse = {
            modelName: name,
            entities,
            inferenceTime: typeof m.inferenceTime === 'number' ? m.inferenceTime : undefined
          };
          return response;
        })
      : [];

    questions.push({ id: questionId, text, modelResponses, humanAnnotations });
  });

  // Use top-level modelNames if provided, otherwise use discovered names
  if (raw.modelNames && Array.isArray(raw.modelNames)) {
    raw.modelNames.forEach((name: any) => {
      if (typeof name === 'string') modelNames.add(name);
    });
  }

  // If no model names were discovered, fall back to metadata.models
  if (modelNames.size === 0 && raw && raw.metadata && Array.isArray(raw.metadata.models)) {
    raw.metadata.models.forEach((m: any) => {
      if (typeof m === 'string') modelNames.add(m);
    });
  }

  // Parse custom label colors if provided
  const customLabelColors: Record<string, string> | undefined = 
    raw.customLabelColors && typeof raw.customLabelColors === 'object'
      ? raw.customLabelColors
      : undefined;

  // Parse saved themes if provided
  const savedThemes = raw.savedThemes && Array.isArray(raw.savedThemes)
    ? raw.savedThemes.filter((t: any) => t.id && t.name && t.bgColor && t.textColor && t.borderColor)
    : undefined;

  return { 
    questions, 
    modelNames: Array.from(modelNames),
    customLabelColors,
    savedThemes
  };
}

// Auto-detect between text format and JSON export
export function parseNERContent(content: string, fileName?: string): ParsedData {
  const trimmed = content.trim();
  const isLikelyJson = (fileName && fileName.toLowerCase().endsWith('.json')) || trimmed.startsWith('{') || trimmed.startsWith('[');
  if (isLikelyJson) {
    try {
      return parseNERJson(content);
    } catch (e) {
      // Fallback to text parser if JSON parsing fails
      return parseNERFile(content);
    }
  }
  return parseNERFile(content);
}

export function getEntityColorClass(label: string, customColors?: Record<string, string>): string {
  // Check for custom color first
  if (customColors && customColors[label]) {
    return customColors[label];
  }
  
  // Normalize: lowercase and remove non-alphanumeric characters
  const normalizedLabel = label.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  // Check for semantic matches (regardless of BIO prefix)
  // Disease/Disorder - RED
  if (normalizedLabel.includes('disease') || normalizedLabel.includes('disorder') || 
      normalizedLabel.includes('condition') || normalizedLabel === 'mentalhealthcondition') {
    return 'entity-disease';
  }
  
  // Symptom/Sign - GREEN
  if (normalizedLabel.includes('symptom') || normalizedLabel.includes('sign') || 
      normalizedLabel === 'physicalcondition') {
    return 'entity-symptom';
  }
  
  // Medication/Drug - BLUE
  if (normalizedLabel.includes('medication') || normalizedLabel.includes('drug') || 
      normalizedLabel.includes('medicine') || normalizedLabel === 'substance') {
    return 'entity-drug';
  }
  
  // Dosage - CYAN
  if (normalizedLabel.includes('dosage') || normalizedLabel.includes('dose')) {
    return 'entity-dosage';
  }
  
  // Treatment/Procedure - PURPLE
  if (normalizedLabel.includes('treatment') || normalizedLabel.includes('procedure') || 
      normalizedLabel.includes('therapeutic') || normalizedLabel.includes('diagnostic') ||
      normalizedLabel === 'treatmenthistory') {
    return 'entity-treatment';
  }
  
  // Anatomy/Body Structure - YELLOW/AMBER
  if (normalizedLabel.includes('anatomy') || normalizedLabel.includes('biological') || 
      normalizedLabel.includes('structure') || normalizedLabel.includes('bodypart') || 
      normalizedLabel.includes('organ') || normalizedLabel === 'nonbiologicallocation') {
    return 'entity-anatomy';
  }
  
  // Time/Date/Duration - ORANGE
  if (normalizedLabel.includes('time') || normalizedLabel.includes('date') || 
      normalizedLabel.includes('duration') || normalizedLabel === 'timeexpression') {
    return 'entity-duration';
  }
  
  // Frequency - LIME
  if (normalizedLabel.includes('frequency')) {
    return 'entity-frequency';
  }
  
  // History/Clinical Context - GRAY (default)
  if (normalizedLabel.includes('history') || normalizedLabel.includes('familyhistory')) {
    return 'entity-default';
  }
  
  // Lab Values/Severity/Age/Sex - GRAY (default)
  if (normalizedLabel.includes('lab') || normalizedLabel.includes('value') || 
      normalizedLabel.includes('severity') || normalizedLabel.includes('age') || 
      normalizedLabel.includes('sex') || normalizedLabel.includes('demographic')) {
    return 'entity-default';
  }
  
  // Clinical Event - GRAY
  if (normalizedLabel.includes('clinical') || normalizedLabel.includes('event')) {
    return 'entity-default';
  }
  
  // Detailed Description/Area/Distance - GRAY
  if (normalizedLabel.includes('detailed') || normalizedLabel.includes('description') || 
      normalizedLabel.includes('area') || normalizedLabel.includes('distance') ||
      normalizedLabel.includes('riskfactor')) {
    return 'entity-default';
  }
  
  // Person/Name (for general NER) - BLUE
  if (normalizedLabel.includes('person') || normalizedLabel === 'per' || 
      normalizedLabel === 'name') {
    return 'entity-person';
  }
  
  // Organization (for general NER) - PURPLE
  if (normalizedLabel.includes('organization') || normalizedLabel === 'org' || 
      normalizedLabel === 'company') {
    return 'entity-organization';
  }
  
  // Location (for general NER) - GREEN
  if (normalizedLabel.includes('location') || normalizedLabel === 'loc' || 
      normalizedLabel === 'place' || normalizedLabel === 'gpe' || 
      normalizedLabel === 'geo') {
    return 'entity-location';
  }
  
  // Money/Currency - LIME
  if (normalizedLabel.includes('money') || normalizedLabel.includes('currency')) {
    return 'entity-money';
  }
  
  // Percent - ORANGE
  if (normalizedLabel.includes('percent')) {
    return 'entity-percent';
  }
  
  // Default for unknown types
  return 'entity-default';
}
